import { config } from '../config/aws-config';
import { getIdToken } from './auth';

const API_BASE_URL = config.apiUrl.replace(/\/+$/, '');

/* ---- Shared types ---- */

export type JobStatus =
  | 'submitted'
  | 'processing'
  | 'awaiting-review'
  | 'manual-review-required'
  | 'approved'
  | 'completed'
  | 'rejected'
  | 'failed';

export type MappingClassification =
  | 'auto-mapped'
  | 'review-recommended'
  | 'warning'
  | 'requires-human-decision'
  | 'not-applicable';

export interface JobListItem {
  jobId: string;
  brokerName: string;
  brokerEmail: string;
  clientType: string | null;
  status: JobStatus;
  createdAt: string;
  updatedAt: string;
  requiresReview: boolean;
  processingTimeMs: number | null;
  processedBy?: string;
  templateMatchInfo?: TemplateMatchInfo | null;
  savedAsTemplate?: boolean;
  templateId?: string | null;
  submissionProgress?: { submitted: number; total: number; failed: number; excluded: number } | null;
}

export interface TransformRule {
  type: 'prefix' | 'suffix' | 'regex' | 'upper' | 'lower' | 'strip' | 'lookup' | 'country' | 'date' | 'boolean' | 'dateFormat' | 'phone' | 'state';
  value?: string;
  pattern?: string;
  replacement?: string;
  map?: Record<string, string>;
}

export interface FieldMapping {
  sourceField: string;
  targetField: string;
  confidenceScore: number;
  classification: MappingClassification;
  sampleValue: string;
  mappedValue: string;
  isModified: boolean;
  transformRule?: TransformRule | null;
}

export interface Recommendation {
  type: 'customer-question' | 'not-needed' | 'manual-review' | 'data-quality';
  priority: 'high' | 'medium' | 'low';
  description: string;
  affectedFields: string[];
  suggestedQuestions?: string[];
}

export interface MappingSummary {
  accountType: string;
  accountTypeJustification: string;
  metrics: {
    totalSourceColumns: number;
    mappedColumns: number;
    unmappedColumns: number;
    totalRecords: number;
    validRecords: number;
    errorRecords: number;
    mappingCompleteness: string;
  };
  overallConfidence: number;
  mappingAccuracy: number;
  overallExplanation: string;
  descriptionSummary: string;
  recommendations: Recommendation[];
  groupingSummary?: GroupingSummary;
}

export interface ValidationResult {
  isValid: boolean;
  errors: { field: string; value: string; message: string }[];
  warnings: { field: string; message: string }[];
  rowCountMatch: boolean;
  requiredFieldsCovered: boolean;
}

export interface GroupDetail {
  accountType: string;
  rowCount: number;
  rowIndices: number[];
  avgConfidence: number;
}

export interface GroupingSummary {
  groups: GroupDetail[];
  unclassifiedRows: number[];
  totalRows: number;
}

export interface GroupResult {
  accountType: string;
  mappings: FieldMapping[];
  validationResults: ValidationResult;
}

export interface RemapStatus {
  accountType: string;
  status: 'in-progress' | 'completed' | 'failed';
  requestedAt: string;
  completedAt: string | null;
  rowIndices: number[];
  previousAccountType: string | null;
  mappingCount: number | null;
  errorMessage: string | null;
}

export interface TemplateMatchInfo {
  templateId: string;
  similarity: number;                // 0.0 - 1.0
  matchedColumns: string[];          // Columns mapped from template
  deltaColumns: string[];            // Columns sent to LLM
  mappingSource: 'template' | 'llm' | 'llm-rerun';
}

export interface JobDetail {
  jobId: string;
  metadata: {
    brokerName: string;
    brokerEmail: string;
    clientType: string | null;
    status: JobStatus;
    createdAt: string;
    updatedAt: string;
  };
  inputFilePreview: string[];
  outputFilePreview: object[];
  mappings: FieldMapping[];
  validationResults: ValidationResult;
  summary: MappingSummary;
  processedBy?: string;
  groupResults?: GroupResult[];
  rowGroupings?: Record<string, number[]>;
  remapStatuses?: RemapStatus[];
  templateMatchInfo?: TemplateMatchInfo | null;
  savedAsTemplate?: boolean;
  templateId?: string | null;
}

export interface ListJobsResponse {
  jobs: JobListItem[];
  total: number;
}

/* ---- Submission types ---- */

export type SubmissionStatus = 'pending' | 'submitted' | 'failed' | 'excluded';

export interface SubmissionRecord {
  recordIndex: number;
  status: SubmissionStatus;
  accountType: string;
  swiftIdApplicationId: string | null;
  submittedAt: string | null;
  error: Record<string, unknown> | null;
  swiftIdResponse: Record<string, unknown> | null;
  recordS3Key: string | null;
}

/* ---- Chat types ---- */

export interface MappingChange {
  sourceField: string;
  targetField: string;
  confidenceScore: number;
  mappedValue: string;
  reason: string;
  transformRule?: TransformRule | null;
}

export interface TransformChange {
  sourceField: string;
  transformRule: TransformRule;
  reason: string;
}

export interface ChatResponse {
  message: string;
  mappingChanges: MappingChange[];
  transformChanges?: TransformChange[];
  updatedGroupingSummary?: {
    groups: GroupDetail[];
    unclassifiedRows: number[];
    totalRows: number;
  };
}

export interface ChatRecord {
  timestamp: string;
  role: 'user' | 'assistant';
  message: string;
  mappingChanges: MappingChange[];
  transformChanges?: TransformChange[];
}

export interface ChatHistoryResponse {
  chatHistory: ChatRecord[];
}

export interface ReassignResponse {
  message: string;
  updatedGroupingSummary?: {
    groups: GroupDetail[];
    unclassifiedRows: number[];
    totalRows: number;
  };
}

/* ---- API helpers ---- */

/** Build Authorization header with Cognito ID token */
async function authHeaders(): Promise<Record<string, string>> {
  const token = await getIdToken();
  return token ? { Authorization: token } : {};
}

export function buildJobListUrl(params: { status?: JobStatus; limit?: number; offset?: number }): string {
  const url = new URL(`${API_BASE_URL}/internal/jobs`);
  if (params.status) url.searchParams.set('status', params.status);
  if (params.limit !== undefined) url.searchParams.set('limit', String(params.limit));
  if (params.offset !== undefined) url.searchParams.set('offset', String(params.offset));
  return url.toString();
}

export async function fetchJobs(params: { status?: JobStatus; limit?: number; offset?: number } = {}): Promise<ListJobsResponse> {
  const url = buildJobListUrl(params);
  const res = await fetch(url, { headers: await authHeaders() });
  if (!res.ok) throw new Error(`Failed to fetch jobs: ${res.status}`);
  return res.json();
}

export async function fetchJobDetail(jobId: string): Promise<JobDetail> {
  const res = await fetch(`${API_BASE_URL}/internal/jobs/${encodeURIComponent(jobId)}`, {
    headers: await authHeaders(),
  });
  if (!res.ok) throw new Error(`Failed to fetch job detail: ${res.status}`);
  return res.json();
}

export async function fetchSubmissions(jobId: string): Promise<SubmissionRecord[]> {
  const res = await fetch(`${API_BASE_URL}/internal/jobs/${encodeURIComponent(jobId)}/submissions`, {
    headers: await authHeaders(),
  });
  if (!res.ok) throw new Error(`Failed to fetch submissions: ${res.status}`);
  return res.json();
}

export async function regenerateOutput(jobId: string): Promise<{ records: object[]; rowGroupings?: Record<string, number[]> }> {
  const res = await fetch(
    `${API_BASE_URL}/internal/jobs/${encodeURIComponent(jobId)}/regenerate-output`,
    {
      method: 'POST',
      headers: await authHeaders(),
    },
  );
  if (!res.ok) throw new Error(`Failed to regenerate output: ${res.status}`);
  return res.json();
}

export async function excludeRecord(jobId: string, recordIndex: number, exclude: boolean): Promise<void> {
  const res = await fetch(
    `${API_BASE_URL}/internal/jobs/${encodeURIComponent(jobId)}/submissions/${recordIndex}/exclude`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
      body: JSON.stringify({ exclude }),
    },
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(err.message || `Exclude record failed: ${res.status}`);
  }
}

export async function updateJobStatus(jobId: string, action: 'approve' | 'reject' | 'complete', reason?: string, accountType?: string, saveAsTemplate?: boolean, templateNotes?: string): Promise<void> {
  const body: Record<string, unknown> = { action, reason };
  if (accountType !== undefined) body.accountType = accountType;
  if (saveAsTemplate !== undefined) body.saveAsTemplate = saveAsTemplate;
  if (templateNotes !== undefined) body.templateNotes = templateNotes;

  const res = await fetch(`${API_BASE_URL}/internal/jobs/${encodeURIComponent(jobId)}/status`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(err.message || `Status update failed: ${res.status}`);
  }
}

export async function saveAsTemplate(jobId: string): Promise<{ templateId: string }> {
  const res = await fetch(`${API_BASE_URL}/internal/jobs/${encodeURIComponent(jobId)}/save-template`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(err.message || `Save as template failed: ${res.status}`);
  }
  return res.json();
}

export async function updateMapping(
  jobId: string,
  sourceField: string,
  targetField: string,
  mappedValue?: string,
  transformRule?: TransformRule | null,
): Promise<void> {
  const body: Record<string, unknown> = { targetField };
  if (mappedValue !== undefined) body.mappedValue = mappedValue;
  if (transformRule !== undefined) body.transformRule = transformRule;

  const res = await fetch(`${API_BASE_URL}/internal/jobs/${encodeURIComponent(jobId)}/mappings/${encodeURIComponent(sourceField)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(err.message || `Mapping update failed: ${res.status}`);
  }
}

export async function postChatMessage(jobId: string, message: string): Promise<ChatResponse> {
  const res = await fetch(`${API_BASE_URL}/internal/jobs/${encodeURIComponent(jobId)}/chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify({ message }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(err.error || `Chat request failed: ${res.status}`);
  }
  return res.json();
}

export async function getChatHistory(jobId: string): Promise<ChatHistoryResponse> {
  const res = await fetch(`${API_BASE_URL}/internal/jobs/${encodeURIComponent(jobId)}/chat`, {
    headers: await authHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(err.error || `Failed to fetch chat history: ${res.status}`);
  }
  return res.json();
}

export async function reassignRows(
  jobId: string,
  rowIndices: number[],
  targetAccountType: string,
): Promise<ReassignResponse> {
  const res = await fetch(
    `${API_BASE_URL}/internal/jobs/${encodeURIComponent(jobId)}/reassign`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
      body: JSON.stringify({ rowIndices, targetAccountType }),
    },
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(err.message || `Reassignment failed: ${res.status}`);
  }
  return res.json();
}

/* ---- Library types ---- */

export interface LibraryEntryListItem {
  templateId: string;
  approvalDate: string;
  status: 'active' | 'inactive';
  originJobId: string;
  accountType: string;
  headerCount: number;
  notes?: string;
}

/* ---- Library API functions ---- */

export async function fetchLibraryEntries(): Promise<LibraryEntryListItem[]> {
  const res = await fetch(`${API_BASE_URL}/internal/library`, {
    headers: await authHeaders(),
  });
  if (!res.ok) throw new Error(`Failed to fetch library entries: ${res.status}`);
  return res.json();
}

export async function updateLibraryEntryStatus(
  templateId: string,
  status: 'active' | 'inactive',
): Promise<void> {
  const res = await fetch(
    `${API_BASE_URL}/internal/library/${encodeURIComponent(templateId)}/status`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
      body: JSON.stringify({ status }),
    },
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(err.message || `Library entry status update failed: ${res.status}`);
  }
}

export async function updateLibraryEntryNotes(
  templateId: string,
  notes: string,
): Promise<void> {
  const res = await fetch(
    `${API_BASE_URL}/internal/library/${encodeURIComponent(templateId)}/status`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
      body: JSON.stringify({ notes }),
    },
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(err.message || `Library entry notes update failed: ${res.status}`);
  }
}

export async function deleteLibraryEntry(templateId: string): Promise<void> {
  const res = await fetch(
    `${API_BASE_URL}/internal/library/${encodeURIComponent(templateId)}`,
    {
      method: 'DELETE',
      headers: await authHeaders(),
    },
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(err.message || `Library entry deletion failed: ${res.status}`);
  }
}

export type ClassificationMode = 'keyword' | 'hybrid' | 'llm';

export async function rerunMapping(jobId: string, classificationMode?: ClassificationMode): Promise<void> {
  const res = await fetch(
    `${API_BASE_URL}/internal/jobs/${encodeURIComponent(jobId)}/rerun`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
      body: JSON.stringify(classificationMode ? { classificationMode } : {}),
    },
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(err.message || `Re-run mapping failed: ${res.status}`);
  }
}

/** Classify a confidence score into the appropriate bucket */
export function classifyConfidence(score: number): MappingClassification {
  if (score >= 90) return 'auto-mapped';
  if (score >= 70) return 'review-recommended';
  if (score >= 50) return 'warning';
  return 'requires-human-decision';
}

/** Return true when a mapping needs engineer attention (< 70%, excluding not-applicable) */
export function needsAttention(mapping: FieldMapping): boolean {
  return mapping.confidenceScore < 70 && mapping.classification !== 'not-applicable';
}

/** Filter jobs that require review */
export function filterReviewJobs(jobs: JobListItem[]): JobListItem[] {
  return jobs.filter(j => j.requiresReview || j.status === 'awaiting-review' || j.status === 'manual-review-required');
}
